// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import styles from '@app/features/theme/components/GlassBackdrop.module.css';
import GlassTheme, {GlassBackdropStyles, GlassWallpaperLayouts} from '@app/features/theme/state/GlassTheme';
import GlassWallpaperImage from '@app/features/theme/state/GlassWallpaperImage';
import Theme from '@app/features/theme/state/Theme';
import {isGlassThemeActive} from '@app/features/theme/utils/GlassThemeUtils';
import {ThemeTypes} from '@konvo/constants/src/UserConstants';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';

/**
 * KONVO: the wallpaper layer behind the app shell. Glass panels only read as
 * glass when there is something to blur, so this sits under everything when
 * the glass theme is active.
 */
export const GlassBackdrop: React.FC = observer(() => {
	if (!isGlassThemeActive()) {
		return null;
	}
	const light = Theme.effectiveTheme === ThemeTypes.GLASS_LIGHT;
	// Your own picture, when chosen and present on this device; otherwise the plain canvas.
	const imageUrl = GlassTheme.backdrop === GlassBackdropStyles.IMAGE ? GlassWallpaperImage.url : null;
	const backdrop =
		GlassTheme.backdrop === GlassBackdropStyles.IMAGE && !imageUrl ? GlassBackdropStyles.FLAT : GlassTheme.backdrop;
	const animate =
		GlassTheme.animateBackdrop && !Accessibility.useReducedMotion && backdrop !== GlassBackdropStyles.FLAT;
	const layout = GlassTheme.wallpaperLayout;
	const grain = GlassTheme.wallpaperGrain / 100;
	const vignette = GlassTheme.wallpaperVignette / 100;
	return (
		<>
			<div
				aria-hidden="true"
				className={clsx(
					styles.backdrop,
					light && styles.light,
					backdrop === GlassBackdropStyles.AURORA && styles.aurora,
					backdrop === GlassBackdropStyles.MESH && styles.mesh,
					backdrop === GlassBackdropStyles.PHOTO && styles.photo,
					backdrop === GlassBackdropStyles.FLAT && styles.flat,
					backdrop === GlassBackdropStyles.IMAGE && styles.image,
					layout === GlassWallpaperLayouts.STUDIO && styles.layoutStudio,
					layout === GlassWallpaperLayouts.HORIZON && styles.layoutHorizon,
					layout === GlassWallpaperLayouts.CENTRE && styles.layoutCentre,
					animate && styles.animate,
				)}
				style={imageUrl ? ({'--wp-image': `url("${imageUrl}")`} as React.CSSProperties) : undefined}
				data-flx="theme.glass-backdrop"
			/>
			{(grain > 0 || vignette > 0) && (
				<div
					aria-hidden="true"
					className={clsx(styles.overlay, light && styles.light)}
					style={{'--wp-grain': grain.toFixed(2), '--wp-vignette': vignette.toFixed(2)} as React.CSSProperties}
					data-flx="theme.glass-backdrop.overlay"
				/>
			)}
		</>
	);
});
